import {
  AddCircleOutlined,
  BusinessOutlined,
  CalendarTodayOutlined,
  DeleteOutlined,
  DescriptionOutlined,
  ExpandMoreOutlined,
  InventoryOutlined,
  RefreshOutlined,
  SendOutlined,
} from '@mui/icons-material'
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material'
import { DeleteButtonWithConfirmation } from 'components/Common/Button/DeleteButtonWithConfirmation'
import { useLocalizedWorkingStepLabel } from 'components/Localization/useLocalizedWorkingStepLabel'
import { useAppController } from 'customHooks/useAppController'
import { useClientStorage } from 'customHooks/useClientStorage'
import { useDialogManager } from 'features/DialogManager/useDialogManager'
import { ProjectSuppliersController } from 'features/ProjectSuppliers/ProjectSuppliersController'
import { BomItemPointer } from 'model/Project/BomItemPointer'
import moment from 'moment'
import { useCallback, useEffect, useMemo, useState } from 'react'
import {
  MoneyDto,
  RequestForQuotationDto,
  RequestForQuotationState,
  WorkingStepType,
} from 'services/APIs/InternalAPI/internal-api.contracts'
import { makeStyles } from 'tss-react/mui'
import { useShowException } from 'utils/useShowException'
import { ProjectRFQLine } from '../Components/ProjectRFQLine'
import { RFQLine } from '../Components/ProjectRFQLines'
import { ProjectRFQController } from '../ProjectRFQController'

export type ManageRFQDialogProps = {
  isOpen: boolean
  onClose: () => void
}

const useStyles = makeStyles({ name: 'ManageRFQDialog' })((theme) => ({
  rfqCard: {
    padding: theme.spacing(2),
    borderRadius: `calc(${theme.shape.borderRadius}px * 2)`,
    border: `1px solid ${theme.palette.divider}`,
    transition: 'border-color 0.2s, box-shadow 0.2s',
    '&:hover': {
      borderColor: theme.palette.primary.main,
      boxShadow: `0 0 0 1px ${theme.palette.primary.main}20`,
    },
  },
  cardHeader: {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: theme.spacing(1),
    width: '100%',
  },
  cardTitle: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    width: '100%',
  },
  cardBody: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: theme.spacing(1.5),
    marginTop: theme.spacing(1.5),
  },
  infoRow: {
    display: 'flex',
    gap: theme.spacing(2),
    flexWrap: 'wrap' as const,
    alignItems: 'center',
  },
  infoItem: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.5),
    color: theme.palette.text.secondary,
  },
  activitiesRow: {
    display: 'flex',
    gap: theme.spacing(0.5),
    flexWrap: 'wrap' as const,
    alignItems: 'center',
  },
  expandToggle: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.5),
    cursor: 'pointer',
    userSelect: 'none',
    color: theme.palette.text.secondary,
    '&:hover': {
      color: theme.palette.text.primary,
    },
  },
  expandIcon: {
    transition: 'transform 200ms',
  },
  expandIconOpen: {
    transform: 'rotate(180deg)',
  },
  emptyState: {
    display: 'flex',
    flexDirection: 'column' as const,
    alignItems: 'center',
    justifyContent: 'center',
    gap: theme.spacing(2),
    padding: theme.spacing(6),
    color: theme.palette.text.secondary,
  },
  groupHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    padding: theme.spacing(1, 0),
    cursor: 'pointer',
    userSelect: 'none',
    '&:hover': {
      color: theme.palette.primary.main,
    },
  },
  groupSection: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: theme.spacing(1.5),
  },
}))

function getStatusColor(
  status: string | undefined,
): 'success' | 'error' | 'warning' | 'info' | 'default' {
  switch (status) {
    case RequestForQuotationState.New:
    case RequestForQuotationState.DocumentCreated:
      return 'info'
    case RequestForQuotationState.RequestForQuotationSent:
    case RequestForQuotationState.Parsed:
      return 'warning'
    case RequestForQuotationState.ProjectCreated:
    case RequestForQuotationState.Accepted:
    case RequestForQuotationState.QuotationResponseSent:
      return 'success'
    case RequestForQuotationState.RequestForQuotationReceivedError:
    case RequestForQuotationState.ParsedError:
    case RequestForQuotationState.ProjectCreatedError:
    case RequestForQuotationState.QuotationResponseSentError:
      return 'error'
    default:
      return 'default'
  }
}

function canSend(status: string | undefined): boolean {
  return (
    status === RequestForQuotationState.New ||
    status === RequestForQuotationState.DocumentCreated
  )
}

function formatMoney(money: MoneyDto | undefined): string {
  if (!money?.value && money?.value !== 0) return '-'
  try {
    return new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency: money.currency || 'EUR',
    }).format(money.value)
  } catch {
    return `${money.value} ${money.currency || ''}`
  }
}

type GroupBy = 'none' | 'supplier' | 'activity' | 'status'

type RFQGroup = {
  key: string
  label: string
  rfqs: RequestForQuotationDto[]
}

function groupRFQs(
  rfqs: RequestForQuotationDto[],
  groupBy: GroupBy,
  supplierMap: Record<string, string>,
  getLocalizedWorkingStepName: (ws: WorkingStepType) => string,
): RFQGroup[] {
  if (groupBy === 'none') {
    return [{ key: 'all', label: '', rfqs }]
  }

  const groups = new Map<string, RequestForQuotationDto[]>()

  if (groupBy === 'supplier') {
    for (const rfq of rfqs) {
      const supplierId = rfq.forwardId ?? 'unassigned'
      const existing = groups.get(supplierId) ?? []
      existing.push(rfq)
      groups.set(supplierId, existing)
    }

    return Array.from(groups.entries()).map(([key, rfqList]) => ({
      key,
      label: key === 'unassigned' ? 'No Supplier' : supplierMap[key] || key,
      rfqs: rfqList,
    }))
  }

  if (groupBy === 'status') {
    for (const rfq of rfqs) {
      const status = rfq.status || 'Unknown'
      const existing = groups.get(status) ?? []
      existing.push(rfq)
      groups.set(status, existing)
    }

    return Array.from(groups.entries()).map(([key, rfqList]) => ({
      key,
      label: key,
      rfqs: rfqList,
    }))
  }

  // group by activity
  for (const rfq of rfqs) {
    const steps = getUniqueWorkingSteps(rfq.bomItemsAndActivities)
    if (steps.length === 0) {
      const existing = groups.get('none') ?? []
      existing.push(rfq)
      groups.set('none', existing)
    } else {
      for (const ws of steps) {
        const existing = groups.get(ws) ?? []
        existing.push(rfq)
        groups.set(ws, existing)
      }
    }
  }

  return Array.from(groups.entries()).map(([key, rfqList]) => ({
    key,
    label:
      key === 'none'
        ? 'No Activities'
        : getLocalizedWorkingStepName(key as WorkingStepType),
    rfqs: rfqList,
  }))
}

function getUniqueWorkingSteps(
  bomItemsAndActivities:
    | { activities?: { workingStepType?: WorkingStepType }[] | undefined }[]
    | undefined,
): WorkingStepType[] {
  if (!bomItemsAndActivities) return []
  const steps = new Set<WorkingStepType>()
  bomItemsAndActivities.forEach((item) =>
    item.activities?.forEach((a) => {
      if (a.workingStepType) steps.add(a.workingStepType)
    }),
  )
  return Array.from(steps)
}

const RFQCard = ({
  rfq,
  loading,
  onSend,
  supplierName,
  onDelete,
}: {
  rfq: RequestForQuotationDto
  loading: Record<string, boolean>
  onSend: (rfq: RequestForQuotationDto) => void
  supplierName: string | undefined
  onDelete: (rfq: RequestForQuotationDto) => void
}) => {
  const { classes, cx } = useStyles()
  const getLocalizedWorkingStepName = useLocalizedWorkingStepLabel()
  const [expanded, setExpanded] = useState(false)

  const rfqData = rfq
  const sendLoading = loading[`sendRequestForQuotation-${rfqData.id}`] || false

  const workingSteps = useMemo(
    () => getUniqueWorkingSteps(rfqData.bomItemsAndActivities),
    [rfqData.bomItemsAndActivities],
  )

  const bomItemCount = rfqData.bomItemsAndActivities?.length ?? 0

  const rfqLines: RFQLine[] = useMemo(
    () =>
      (rfqData.bomItemsAndActivities ?? []).map((item) => ({
        bomItemPointer: { id: item.bomItemId } as BomItemPointer,
        activities: item.activities ?? [],
      })),
    [rfqData.bomItemsAndActivities],
  )

  return (
    <Paper className={classes.rfqCard} elevation={0}>
      {/* Header: title + status + action */}
      <div className={classes.cardHeader}>
        <div
          className={classes.cardTitle}
          style={{
            width: '100%',
            display: 'flex',
            justifyContent: 'space-between',
          }}
        >
          <Typography
            variant="subtitle1"
            sx={{ fontWeight: 600, width: '100%' }}
            noWrap
          >
            {`${supplierName} - ${rfqData.bomItemsAndActivities?.length || 0} item(s)`}
          </Typography>
          <Chip
            label={rfqData.status || 'Unknown'}
            size="small"
            sx={{ justifySelf: 'flex-end' }}
            color={getStatusColor(rfqData.status)}
          />
        </div>
      </div>

      {/* Body: details */}
      <div className={classes.cardBody}>
        {/* Info row: supplier, buyer, dates, bom items */}
        <div className={classes.infoRow}>
          {(supplierName || rfqData.forwardId) && (
            <Tooltip title="Supplier">
              <span className={classes.infoItem}>
                <BusinessOutlined sx={{ fontSize: 16 }} />
                <Typography variant="body2">
                  {supplierName || rfqData.forwardId}
                </Typography>
              </span>
            </Tooltip>
          )}

          {rfqData.buyerName && (
            <Tooltip title="Buyer">
              <span className={classes.infoItem}>
                <DescriptionOutlined sx={{ fontSize: 16 }} />
                <Typography variant="body2">{rfqData.buyerName}</Typography>
              </span>
            </Tooltip>
          )}

          <Tooltip title="Created">
            <span className={classes.infoItem}>
              <CalendarTodayOutlined sx={{ fontSize: 16 }} />
              <Typography variant="body2">
                {moment(rfqData.dateCreated).format('L LT')}
              </Typography>
            </span>
          </Tooltip>

          {bomItemCount > 0 && (
            <Tooltip title="BOM items">
              <span className={classes.infoItem}>
                <InventoryOutlined sx={{ fontSize: 16 }} />
                <Typography variant="body2">
                  {bomItemCount} item{bomItemCount !== 1 ? 's' : ''}
                </Typography>
              </span>
            </Tooltip>
          )}
        </div>

        {/* Activities */}
        {workingSteps.length > 0 && (
          <div className={classes.activitiesRow}>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ mr: 0.5 }}
            >
              Activities:
            </Typography>
            {workingSteps.map((ws) => (
              <Chip
                key={ws}
                label={getLocalizedWorkingStepName(ws)}
                size="small"
                variant="outlined"
              />
            ))}
          </div>
        )}

        {/* BOM Items expandable section */}
        {bomItemCount > 0 && (
          <>
            <Divider />
            <div
              className={classes.expandToggle}
              onClick={() => setExpanded((v) => !v)}
            >
              <ExpandMoreOutlined
                fontSize="small"
                className={cx(
                  classes.expandIcon,
                  expanded && classes.expandIconOpen,
                )}
              />
              <Typography variant="caption" sx={{ fontWeight: 600 }}>
                {bomItemCount} BOM Item{bomItemCount !== 1 ? 's' : ''}
              </Typography>
            </div>

            <Collapse in={expanded}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Item</TableCell>
                    <TableCell align="right">Activities</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rfqLines.map((line, index) => (
                    <ProjectRFQLine
                      key={line.bomItemPointer?.id || index}
                      bomItemPointer={line.bomItemPointer}
                      line={line}
                      index={index}
                    />
                  ))}
                </TableBody>
              </Table>
            </Collapse>
          </>
        )}
      </div>
      <div
        style={{
          display: 'flex',
          justifyContent: 'flex-end',
          marginTop: 16,
          gap: '14px',
        }}
      >
        <DeleteButtonWithConfirmation
          title="Delete RFQ"
          onDelete={() => onDelete(rfqData)}
          buttonType="button"
          muiButtonProps={{
            disabled: loading[`deleteRfq-${rfqData.id}`],
            startIcon: loading[`deleteRfq-${rfqData.id}`] ? (
              <CircularProgress size={16} color="inherit" />
            ) : (
              <DeleteOutlined fontSize="small" />
            ),
          }}
        />
        {canSend(rfqData.status) && (
          <Tooltip title="Send to supplier">
            <span>
              <Button
                variant="contained"
                size="small"
                color="primary"
                startIcon={
                  sendLoading ? (
                    <CircularProgress size={16} color="inherit" />
                  ) : (
                    <SendOutlined fontSize="small" />
                  )
                }
                disabled={sendLoading}
                onClick={() => onSend(rfqData)}
              >
                Send
              </Button>
            </span>
          </Tooltip>
        )}
      </div>
    </Paper>
  )
}

function resolveSupplierName(
  rfq: RequestForQuotationDto,
  supplierMap: Record<string, string>,
): string | undefined {
  return (
    supplierMap[rfq.forwardId ?? ''] ||
    supplierMap[rfq.buyerRelationId ?? ''] ||
    supplierMap[rfq.forwardSender ?? ''] ||
    undefined
  )
}

const RFQGroupSection = ({
  group,
  showHeader,
  loading,
  onSend,
  onDelete,
  supplierMap,
}: {
  group: RFQGroup
  showHeader: boolean
  loading: Record<string, boolean>
  onSend: (rfq: RequestForQuotationDto) => void
  onDelete: (rfq: RequestForQuotationDto) => void
  supplierMap: Record<string, string>
}) => {
  const { classes, cx } = useStyles()
  const [expanded, setExpanded] = useState(true)

  return (
    <div className={classes.groupSection}>
      {showHeader && (
        <div
          className={classes.groupHeader}
          onClick={() => setExpanded((v) => !v)}
        >
          <ExpandMoreOutlined
            fontSize="small"
            className={cx(
              classes.expandIcon,
              expanded && classes.expandIconOpen,
            )}
          />
          <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
            {group.label}
          </Typography>
          <Chip label={group.rfqs.length} size="small" variant="outlined" />
        </div>
      )}

      <Collapse in={!showHeader || expanded}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          {group.rfqs.map((rfq) => (
            <RFQCard
              key={rfq.id}
              rfq={rfq}
              loading={loading}
              onSend={onSend}
              onDelete={onDelete}
              supplierName={resolveSupplierName(rfq, supplierMap)}
            />
          ))}
        </Box>
      </Collapse>
    </div>
  )
}

export const ManageRFQDialog = (props: ManageRFQDialogProps) => {
  const { classes } = useStyles()
  const { openDialog } = useDialogManager()
  const { controller, loading } = useAppController(
    () => new ProjectRFQController(),
  )
  const { controller: suppliersController } = useAppController(
    () => new ProjectSuppliersController(),
  )

  const [rfqs, setRFQs] = useState<RequestForQuotationDto[]>([])
  const [supplierMap, setSupplierMap] = useState<Record<string, string>>({})
  const [groupBy, setGroupBy] = useClientStorage<GroupBy>(
    'manage-rfq-group-by',
    'none',
  )
  const getLocalizedWorkingStepName = useLocalizedWorkingStepLabel()

  const groups = useMemo(
    () => groupRFQs(rfqs, groupBy, supplierMap, getLocalizedWorkingStepName),
    [rfqs, groupBy, supplierMap, getLocalizedWorkingStepName],
  )

  const ShowException = useShowException('Request for Quotations')
  const isLoading = loading['getRFQs']

  const fetchSuppliers = useCallback(async () => {
    try {
      const resp = await suppliersController.getSuppliers()
      const map: Record<string, string> = {}
      for (const s of resp?.projectSuppliers ?? []) {
        const name = s.name || s.glNumber || s.id || ''
        if (s.glNumber) {
          map[s.glNumber] = name
        }
        if (s.id) {
          map[s.id] = name
        }
      }
      setSupplierMap(map)
    } catch {
      // non-critical, we fall back to forwardId
    }
  }, [suppliersController])

  const fetchData = useCallback(async () => {
    try {
      const data = await controller.getRFQs()
      setRFQs(data.details ?? [])
    } catch (err) {
      ShowException(err)
    }
  }, [controller, ShowException])

  useEffect(() => {
    if (props.isOpen) {
      console.log('here')
      fetchData()
      fetchSuppliers()
    }
  }, [props.isOpen, fetchData, fetchSuppliers])

  const handleSendRFQ = useCallback(
    async (rfq: RequestForQuotationDto) => {
      try {
        const resp = await controller.sendRequestForQuotation({
          requestForQuotationId: rfq.id,
          forwardId: rfq.forwardId,
        })

        if (!resp.success) {
          throw new Error(resp.message || 'Failed to send RFQ')
        }

        fetchData()
      } catch (err) {
        ShowException(err)
      }
    },
    [controller, fetchData, ShowException],
  )

  const handleDeleteRFQ = useCallback(
    async (rfq: RequestForQuotationDto) => {
      try {
        await controller.deleteRFQ(rfq.id)
        setRFQs((prev) => prev.filter((r) => r.id !== rfq.id))
      } catch (err) {
        ShowException(err)
      } finally {
        fetchData()
      }
    },
    [controller, ShowException, fetchData],
  )

  const handleOpenDraftDialog = useCallback(() => {
    openDialog('DraftRFQDialog')
  }, [openDialog])

  return (
    <Dialog
      open={props.isOpen}
      onClose={props.onClose}
      fullWidth
      maxWidth="md"
      slotProps={{
        paper: { sx: { minHeight: '50vh', maxHeight: '80vh' } },
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          pb: 1,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Typography variant="h6" component="span">
            Request for Quotations
          </Typography>
          <Chip
            label={rfqs.length}
            size="small"
            color="primary"
            variant="outlined"
          />
        </Box>

        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          <ToggleButtonGroup
            value={groupBy}
            exclusive
            onChange={(_, value) => value && setGroupBy(value)}
            size="small"
          >
            <ToggleButton value="none">None</ToggleButton>
            <ToggleButton value="supplier">Supplier</ToggleButton>
            <ToggleButton value="activity">Activity</ToggleButton>
            <ToggleButton value="status">Status</ToggleButton>
          </ToggleButtonGroup>
          <Tooltip title="Refresh">
            <IconButton onClick={fetchData} disabled={isLoading} size="small">
              {isLoading ? (
                <CircularProgress size={18} />
              ) : (
                <RefreshOutlined fontSize="small" />
              )}
            </IconButton>
          </Tooltip>
          <Button
            variant="contained"
            size="small"
            startIcon={<AddCircleOutlined />}
            onClick={handleOpenDraftDialog}
          >
            New RFQ
          </Button>
        </Box>
      </DialogTitle>

      <DialogContent
        sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, pt: 1 }}
      >
        {rfqs.length === 0 && !isLoading && (
          <div className={classes.emptyState}>
            <Typography variant="body1">No RFQs found</Typography>
            <Typography variant="body2">
              Create a new RFQ to get started
            </Typography>
            <Button
              variant="outlined"
              startIcon={<AddCircleOutlined />}
              onClick={handleOpenDraftDialog}
            >
              Create RFQ
            </Button>
          </div>
        )}

        {groups.map((group) => (
          <RFQGroupSection
            key={group.key}
            group={group}
            showHeader={groupBy !== 'none'}
            loading={loading}
            onSend={handleSendRFQ}
            onDelete={handleDeleteRFQ}
            supplierMap={supplierMap}
          />
        ))}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 1.5 }}>
        <Button variant="outlined" onClick={props.onClose}>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export const OpenManageRFQDialogButton = () => {
  const { openDialog } = useDialogManager()

  return (
    <Button onClick={() => openDialog('ManageRFQDialog')}>Manage RFQs</Button>
  )
}
